import { api } from '../../api/client';
import { useLoader } from '../../app/hooks';
import { countBodyParams } from '../whatsapp/Messaging';

export interface PhoneChoice {
  id: string;
  accountId: string;
  label: string;
}
export interface TemplateChoice {
  accountId: string;
  name: string;
  language: string;
  status: string;
  bodyParams: number;
}

interface AccountRow {
  id: string;
  phoneNumbers: { id: string; displayPhoneNumber: string; verifiedName: string | null }[];
}
interface TemplateRow {
  accountId: string;
  name: string;
  language: string;
  status: string;
  components: unknown;
}

/** The WhatsApp numbers and templates a flow step can pick from. */
export function useWhatsappChoices(tenantId: string | undefined, enabled: boolean) {
  const data = useLoader(async () => {
    if (!enabled) return { phones: [] as PhoneChoice[], templates: [] as TemplateChoice[] };
    const [accounts, templates] = await Promise.all([
      api.get<{ accounts: AccountRow[] }>('/whatsapp/accounts'),
      api.get<{ templates: TemplateRow[] }>('/whatsapp/templates'),
    ]);
    return {
      phones: accounts.accounts.flatMap((a) =>
        a.phoneNumbers.map((p) => ({
          id: p.id,
          accountId: a.id,
          label: `${p.displayPhoneNumber}${p.verifiedName ? ` · ${p.verifiedName}` : ''}`,
        })),
      ),
      templates: templates.templates.map((t) => ({
        accountId: t.accountId,
        name: t.name,
        language: t.language,
        status: t.status,
        bodyParams: countBodyParams(t.components),
      })),
    };
  }, tenantId);
  return data;
}

/** Approved templates of the account a number belongs to. */
export function templatesForPhone(
  phones: PhoneChoice[],
  templates: TemplateChoice[],
  phoneId: string,
): TemplateChoice[] {
  const accountId = phones.find((p) => p.id === phoneId)?.accountId;
  return templates.filter((t) => t.accountId === accountId && t.status === 'APPROVED');
}
